import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { MsalProvider } from "@azure/msal-react";
import { CacheLookupPolicy } from "@azure/msal-browser";
import {
  createMsalSimDocumentsInstance,
  createMsalSimInstance,
  loginRequest,
} from "./authentication/authConfig";
document.title = `SIM Documents ${process.env.REACT_APP_ENV == "PRD" ? "" : process.env.REACT_APP_ENV}`;
const msalSimDocumentsInstance = createMsalSimDocumentsInstance();
const msalSimInstance = createMsalSimInstance();

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

/**
 * Signing in requires two logins: SIM Documents (started by the Sign in button) and SIM.
 * Run them in a fixed order before rendering, so the SIM login only starts once both
 * instances are initialized and the SIM Documents redirect has been processed.
 */
async function bootstrap() {
  await msalSimDocumentsInstance.initialize();
  await msalSimInstance.initialize();

  // Each instance only handles a redirect response for a login it started itself.
  await msalSimDocumentsInstance.handleRedirectPromise();
  await msalSimInstance.handleRedirectPromise();

  // The account record is shared by both instances, so look it up once and set it on both.
  const account =
    msalSimDocumentsInstance.getActiveAccount() ??
    msalSimDocumentsInstance.getAllAccounts()[0];

  if (account) {
    msalSimDocumentsInstance.setActiveAccount(account);
    msalSimInstance.setActiveAccount(account);

    // Tokens are stored per app registration: check the SIM ones exist. Only look in the
    // cache (no hidden iframe fallback), so the outcome doesn't depend on browser cookie rules.
    const hasSimTokens = await msalSimInstance
      .acquireTokenSilent({
        scopes: [process.env.REACT_APP_SIM_SCOPE!],
        account,
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      })
      .then(() => true)
      .catch(() => false);

    if (!hasSimTokens) {
      // The Microsoft session cookie from the first login lets this pass without a login form.
      await msalSimInstance.loginRedirect({
        ...loginRequest,
        loginHint: account.username,
      });
      return; // The page navigates away, don't render.
    }
  }

  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalSimInstance}>
        <MsalProvider instance={msalSimDocumentsInstance}>
          <App />
        </MsalProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}

bootstrap().catch((error) => console.error("MSAL bootstrap error:", error));

reportWebVitals();
